import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const EVENT_ROWS = [
  { key: 'document_request', label: 'Document Requested', icon: 'fa-folder-plus', desc: "Doc team asks the student to upload a file" },
  { key: 'invoice_raised', label: 'Invoice Raised', icon: 'fa-file-invoice-dollar', desc: "Finance team issues a new invoice" },
  { key: 'payment_reminder', label: 'Payment Reminder', icon: 'fa-bell', desc: "Auto / manual nudge for unpaid invoices · includes Razorpay payment link for the exact balance" },
  { key: 'agreement_published', label: 'Agreement Published', icon: 'fa-file-signature', desc: "A new consent / agreement needs the student's signature" },
];

const DEFAULT_EVENTS = {
  document_request: true,
  invoice_raised: true,
  payment_reminder: true,
  agreement_published: true,
  admin_message: false,
  stage_change: false,
};

const DEFAULT_TEMPLATES = {
  document_request:    { template: 'student_document_request',    params: ['name', 'document_name', 'portal_url'] },
  invoice_raised:      { template: 'student_invoice_raised',      params: ['name', 'invoice_number', 'amount', 'payment_link'] },
  payment_reminder:    { template: 'payment_reminder',            params: ['name', 'invoice_number', 'balance', 'payment_link'] },
  agreement_published: { template: 'student_agreement_published', params: ['name', 'title', 'portal_url'] },
  admin_message:       { template: 'student_admin_message',       params: ['name', 'sender', 'preview', 'portal_url'] },
  stage_change:        { template: 'student_stage_change',        params: ['name', 'stage_label', 'portal_url'] },
};

export default function StudentWATINotifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [events, setEvents] = useState(DEFAULT_EVENTS);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [recent, setRecent] = useState([]);
  const [testing, setTesting] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [templateStatuses, setTemplateStatuses] = useState({});
  const [checkingStatuses, setCheckingStatuses] = useState(false);

  const checkTemplateStatuses = async () => {
    setCheckingStatuses(true);
    try {
      const res = await apiClient.get('/settings/student-wati-notifications/template-status');
      setTemplateStatuses(res.data.results || {});
      if (res.data.status === 'credentials_missing') {
        toast.error('WATI credentials not configured. Set them in Settings > Integrations.');
      } else if (res.data.status === 'fetch_failed') {
        toast.error(`Template approval is unknown: ${(res.data.connection?.state || 'connection failed').replaceAll('_', ' ')}`);
      } else {
        toast.success(`Checked ${Object.keys(res.data.results || {}).length} templates against WATI`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check template statuses');
    } finally {
      setCheckingStatuses(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, logRes] = await Promise.all([
        apiClient.get('/settings/student-wati-notifications'),
        apiClient.get('/settings/student-wati-notifications/log?limit=10').catch(() => ({ data: { logs: [] } })),
      ]);
      setEnabled(cfgRes.data.enabled !== false);
      setEvents({ ...DEFAULT_EVENTS, ...(cfgRes.data.events || {}), admin_message: false, stage_change: false });
      setTemplates({ ...DEFAULT_TEMPLATES, ...(cfgRes.data.templates || {}) });
      setRecent(logRes.data.logs || []);
    } catch (err) {
      // keep defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // NOTE: We intentionally do NOT auto-check template statuses on mount.
    // Checking hits the WATI API, so it only runs when the admin clicks
    // "Check Templates" (or Send Test). Keeps opening this screen instant + quota-friendly.
  }, []); // eslint-disable-line

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      await apiClient.put('/settings/student-wati-notifications', patch);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAll = async (next) => {
    setEnabled(next);
    await saveConfig({ enabled: next });
  };

  const handleToggleEvent = async (key) => {
    const nextEvents = { ...events, [key]: !events[key] };
    setEvents(nextEvents);
    await saveConfig({ events: nextEvents });
  };

  const handleTemplateNameChange = (key, value) => {
    setTemplates(prev => ({ ...prev, [key]: { ...prev[key], template: value } }));
  };

  const saveTemplates = async () => {
    await saveConfig({ templates });
  };

  const handleSendTest = async (eventKey) => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number first');
      return;
    }
    setTesting(prev => ({ ...prev, [eventKey]: true }));
    try {
      const res = await apiClient.post('/settings/student-wati-notifications/test', {
        phone: testPhone.trim(),
        event: eventKey,
      });
      const last = res.data.last_log;
      if (last?.status === 'sent') {
        toast.success(`Test sent to ${last.phone} via ${last.message_preview?.startsWith('[template') ? 'template' : 'session text'}`);
      } else {
        toast.error(`Test failed: ${last?.error || 'unknown error'}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test');
    } finally {
      setTesting(prev => ({ ...prev, [eventKey]: false }));
    }
  };

  return (
    <Card data-testid="student-wati-notifications-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <i className="fab fa-whatsapp text-green-600 text-lg"></i>
            Student WhatsApp Alerts
          </CardTitle>
          <CardDescription>
            Auto-send WhatsApp messages to students via WATI whenever the team takes a key action — so they log in and respond faster.
          </CardDescription>
        </div>
        <div className="flex items-center gap-3 rounded-lg border bg-slate-50 px-3 py-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={checkTemplateStatuses}
            disabled={checkingStatuses}
            className="border-green-200 text-green-700 hover:bg-green-50"
            data-testid="check-templates-btn"
          >
            {checkingStatuses ? (
              <><i className="fas fa-spinner fa-spin mr-1.5"></i>Checking...</>
            ) : (
              <><i className="fas fa-sync mr-1.5"></i>Check Templates</>
            )}
          </Button>
          <span className="text-xs text-gray-600">{enabled ? 'On' : 'Off'}</span>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggleAll}
            disabled={loading || saving}
            data-testid="wati-alerts-master-toggle"
          />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm">
            <i className="fas fa-spinner fa-spin mr-2"></i>Loading...
          </div>
        ) : (
          <>
            <div className={`space-y-2 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}>
              {EVENT_ROWS.map(row => {
                const tpl = templates[row.key] || DEFAULT_TEMPLATES[row.key];
                const statusInfo = templateStatuses[row.key];
                const statusCfg = {
                  approved: { dot: 'bg-emerald-500', label: 'Approved', badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'fa-check-circle' },
                  pending:  { dot: 'bg-amber-500',   label: 'Pending',  badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',     icon: 'fa-clock' },
                  rejected: { dot: 'bg-red-500',     label: 'Rejected', badgeClass: 'bg-red-50 text-red-700 border-red-200',           icon: 'fa-ban' },
                  missing:  { dot: 'bg-gray-400',    label: 'Missing',  badgeClass: 'bg-gray-100 text-gray-600 border-gray-300',       icon: 'fa-exclamation-circle' },
                  error:    { dot: 'bg-red-400',     label: 'Error',    badgeClass: 'bg-red-50 text-red-700 border-red-200',           icon: 'fa-times' },
                  unknown:  { dot: 'bg-amber-400', label: 'Unknown · verify connection', badgeClass: 'bg-amber-50 text-amber-800 border-amber-200', icon: 'fa-question-circle' },
                }[statusInfo?.status] || null;
                return (
                  <div
                    key={row.key}
                    className="rounded-lg border bg-white p-3 hover:border-emerald-200"
                    data-testid={`wati-event-row-${row.key}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                          <i className={`fas ${row.icon} text-green-600 text-sm`}></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800">{row.label}</div>
                          <div className="text-[11px] text-gray-500">{row.desc}</div>
                        </div>
                      </div>
                      <Switch
                        checked={events[row.key] !== false}
                        onCheckedChange={() => handleToggleEvent(row.key)}
                        disabled={saving}
                        data-testid={`wati-event-toggle-${row.key}`}
                      />
                    </div>
                    {events[row.key] !== false && (
                      <div className="mt-3 pt-3 border-t border-dashed flex flex-col sm:flex-row sm:items-end gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Label className="text-[10px] uppercase tracking-wide text-gray-500">WATI Template Name</Label>
                            {statusCfg && (
                              <Badge
                                variant="outline"
                                className={`text-[9px] ${statusCfg.badgeClass}`}
                                title={statusInfo?.detail || statusCfg.label}
                                data-testid={`wati-template-status-${row.key}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full mr-1 ${statusCfg.dot}`}></span>
                                {statusCfg.label}
                              </Badge>
                            )}
                          </div>
                          <Input
                            value={tpl.template}
                            onChange={(e) => handleTemplateNameChange(row.key, e.target.value)}
                            onBlur={() => { saveTemplates(); }}
                            placeholder="e.g. student_document_request"
                            className="h-8 text-sm font-mono bg-slate-50"
                            data-testid={`wati-template-name-${row.key}`}
                          />
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            <span className="text-[10px] text-gray-500">Placeholders:</span>
                            {(tpl.params || []).map((p, i) => (
                              <Badge key={p} variant="outline" className="text-[9px] bg-slate-50 border-slate-200 text-slate-700">
                                {`{{${i + 1}}}`} = {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendTest(row.key)}
                          disabled={testing[row.key]}
                          className="shrink-0 border-green-200 text-green-700 hover:bg-green-50"
                          data-testid={`wati-test-${row.key}`}
                        >
                          {testing[row.key] ? (
                            <><i className="fas fa-spinner fa-spin mr-1.5"></i>Sending...</>
                          ) : (
                            <><i className="fab fa-whatsapp mr-1.5"></i>Send Test</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 rounded-lg border bg-slate-50 p-3 flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1 min-w-0">
                <Label htmlFor="wati-test-phone" className="text-[10px] uppercase tracking-wide text-gray-500">Test Phone (with country code, e.g. 917594882898)</Label>
                <Input
                  id="wati-test-phone"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="917594882898"
                  className="h-9 text-sm bg-white"
                  data-testid="wati-test-phone-input"
                />
              </div>
              <span className="text-[11px] text-gray-500 sm:max-w-xs">
                Enter any phone number above, then click <strong>Send Test</strong> next to any event to verify delivery before going live.
              </span>
            </div>

            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2">
              <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
              <div className="text-[11px] text-blue-800 leading-snug">
                <strong>Delivery strategy:</strong> alerts go out through your <strong>Patrick's WATI</strong> account. We first try the <strong>approved WATI template</strong> configured per event (works 24/7) and fall back to a plain-text session message if the template isn't live yet (works only if the student messaged you in the last 24h). Create each template in your WATI dashboard with the exact name shown above and the same placeholder order.
              </div>
            </div>

            {recent.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">Recent Notifications</h4>
                  <Button variant="outline" size="sm" onClick={load} data-testid="refresh-wati-log-btn">
                    <i className="fas fa-sync mr-1"></i>Refresh
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {recent.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-2 text-[11px] rounded border bg-white px-2.5 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge
                          variant="outline"
                          className={`text-[9px] ${r.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                        >
                          {r.status === 'sent' ? '✓ Sent' : r.status === 'failed' ? '✗ Failed' : r.status}
                        </Badge>
                        <span className="text-gray-500 shrink-0">{r.event}</span>
                        <span className="text-gray-700 font-mono">{r.phone || '—'}</span>
                        <span className="text-gray-400 truncate">{r.message_preview}</span>
                      </div>
                      <span className="text-[9px] text-gray-400 shrink-0">
                        {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
